import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { CHARACTER_KEYS, FONT_SCALE, PARAGRAPH_STYLE_KEY } from '../constants';
import { IAllStyles, IParagraphStyle } from '../interface/editor-styles.interface';
import { merge } from './utils';

/**
 * 过滤掉 style 中非段落属性
 * @param style
 * @returns
 */
function filterParagraphStyle(style: IAllStyles) {
    return Object.entries(style).reduce((result, [currentKey, currentValue]) => {
        if (PARAGRAPH_STYLE_KEY.includes(currentKey)) {
            result[currentKey] = currentValue;
        }
        return result;
    }, {});
}


// 对于项目类型这种东西。。。继承的时候还要判断一下
function getFixedInheritStyle(raw, inherit) {
    if (!raw || !inherit) return inherit;

    const inheritStyle = { ...inherit };
    CHARACTER_KEYS.forEach(key => delete inheritStyle[key]);
    return inheritStyle;
}

/**
 * 处理需要响应 FONT_SCALE 的属性
 * @param styles
 */
function processNeedScaledStyle(styles: IParagraphStyle): IParagraphStyle {
    const newStyle: any = {};
    // 需要缩放的值
    const needScale = [
        'marginLeft',
        'textIndent',
        'tabSize',
    ];
    needScale.forEach((key) => {
        if (styles[key]) {
            newStyle[key] = styles[key] * FONT_SCALE;
        }
    });
    if (styles.paddingTop && styles.paddingTopIs === 'absolute') {
        newStyle.paddingTop = styles.paddingTop * FONT_SCALE;
    }
    if (styles.paddingBottom && styles.paddingBottomIs === 'absolute') {
        newStyle.paddingBottom = styles.paddingBottom * FONT_SCALE;
    }
    if (styles.tabList) {
        newStyle.tabList = styles.tabList.map(tabstop => ({ ...tabstop, position: tabstop.position * FONT_SCALE }));
    }
    if (styles.lineHeightIs === 'absolute' && styles.lineHeight) {
        newStyle.lineHeight = styles.lineHeight * FONT_SCALE;
    }
    return {
        ...styles,
        ...newStyle,
    };
}

/**
 * 根据继承关系，获取para上应该挂载的style
 * TODO 另外会给line-height加默认值...这个太蠢了
 * @param {Object} paraStyle para上的style
 * @param {Number} level para的level
 * @param {Array} styleList 继承列表
 * @param noRawData 转换是否存储rawData
 * @returns {Object} para上挂载的style
 */
export function combineParaStyles(paraStyle, noRawData = false) {
    let styles = paraStyleMerge({
        PPTLineHeight: 1,
        lineHeight: 1.2,
        textAlign: 'left',
    }, paraStyle);

    // noRawData 为 true 表示不需要保存原始数据
    // noRawData 为 false 表示需要保存原始数据
    // 是否需要添加标记，用于识别当前属性是否来自于自身
    const needFlagForIdentifyWhetherPropsComeFromItself = !noRawData;
    if (needFlagForIdentifyWhetherPropsComeFromItself) {
        paraStyle && Object.keys(paraStyle).forEach((key) => {
            styles[`raw-${key}`] = true;
        });
    }

    styles = processNeedScaledStyle(styles);

    return styles;
}

export function paraStyleMerge(raw, ...styles) {
    return styles.reduce((prev, style) => merge(prev, style), raw);
}


/**
 * 根据代码逻辑来看，这个函数主要是处理段前段后间距，以下是推测的背景
 * 段前段后数据有两种：
 *  - 固定值
 *  - 百分比，会随着当前段落某些数据动态变化
 *      如果是百分比的话，就存在一个问题：无法用 paddingTop 值直接渲染，而是需要动态计算然后再渲染。
 *          在之前 dom 版本中，最终渲染是借用了 css 的 padding 属性，所以需要在渲染前计算真正的数值然后再覆盖 padding 属性，这样又导致一个问题就是数据保存的时候，没办法还原到原始值，
 *          为了解决数据保存还原的问题，就新增了一个值(之前是叫做 rawPaddingTop)用于原始的 padding 值，直接使用这个值进行保存，而原有的 paddingTop 属性保存的是最终渲染需要的数据。
 *      这个实现方案的问题在于把一个渲染时动态计算的值存在内存中了，所以在修改 paddingTop 值或修改段落第一个 textRun 样式时，也需要同时更新这个计算值，才能保证渲染和保存都不出错。
 *
 * 理论上来说这里比较合适的处理方式应该是把 paddingTop 和 paddingTopIs 数据传递给排版层，在排版时动态计算，类似于当前多倍行距的处理方式。
 * 目前排版层不支持百分比形式的段前段后，暂时先沿用之前的这个处理方式，后续可以优化
 */
export function fixParaStyle(paraStyle, maxFontSize) {
    const style = { ...paraStyle };

    // paddingTop与paddingBottom的处理
    // 使用该段中最大的字体大小来计算 固定值*百分比
    if (expect(style.paddingTop).isNumber()) {
        style.rawPaddingTop = style.paddingTop;
        if (style.paddingTopIs === 'percent') style.paddingTop *= (maxFontSize * 1.2 * FONT_SCALE); // 固定值高度为单倍行高，即1.2fontSize
    }
    if (expect(style.paddingBottom).isNumber()) {
        style.rawPaddingBottom = style.paddingBottom;
        if (style.paddingBottomIs === 'percent') style.paddingBottom *= (maxFontSize * 1.2 * FONT_SCALE); // 固定值高度为单倍行高，即1.2fontSize
    }

    return style;
}

/**
 * 处理缩放之后的属性
 * @param style
 * @returns
 */
// eslint-disable-next-line complexity
function processScaledStyle(style: IParagraphStyle) {
    const newStyle: any = {};
    const toBeApplied = [
        'marginLeft',
        'textIndent',
        'tabSize',
    ];
    toBeApplied.forEach((key) => {
        if (style[key]) {
            newStyle[key] = style[key] / FONT_SCALE;
        }
    });

    if (style.paddingTop && style.paddingTopIs === 'absolute') {
        newStyle.paddingTop = style.paddingTop / FONT_SCALE;
    }
    if (style.paddingBottom && style.paddingBottomIs === 'absolute') {
        newStyle.paddingBottom = style.paddingBottom / FONT_SCALE;
    }
    if (style.rawPaddingTop && style.paddingTopIs === 'absolute') {
        newStyle.rawPaddingTop = style.rawPaddingTop / FONT_SCALE;
    }
    if (style.rawPaddingBottom && style.paddingBottomIs === 'absolute') {
        newStyle.rawPaddingBottom = style.rawPaddingBottom / FONT_SCALE;
    }
    if (style.tabList) {
        newStyle.tabList = style.tabList.map(tabstop => ({ ...tabstop, position: tabstop.position / FONT_SCALE }));
    }

    if (style.lineHeightIs === 'absolute' && style.lineHeight) {
        newStyle.lineHeight = style.lineHeight / FONT_SCALE;
    }
    return {
        ...style,
        ...newStyle,
    };
}

/**
 * 部分style设置在para上，需要转换回到text para上
 * 转换回部分需要转换的styles
 * text-align
 * @param excludeInheritedData 为 true 表示只保留自身的属性，不保留继承的
 */
export function convertParaStyles({ level = null, style = null }: {
    level?: number | null;
    style?: any;
} = {}, excludeInheritedData = true): IParagraphStyle | null {
    if (!style) return null;

    let reservedStyle: any = {
        level,
    };
    // 根据 excludeInheritedData 参数决定需要保留哪些属性
    const needReservedKeys = excludeInheritedData
        ? Object.keys(style).filter(key => style[`raw-${key}`])
        // 如果要保留继承属性，那么 raw- 开头的标志也要保留，用于在 ui=>sdk 时筛选出自身属性用于保存
        : Object.keys(style);
    needReservedKeys.forEach((key) => {
        // 处理 paddingTop paddingBottom，具体原因见 fixParaStyle 的注释
        if (key === 'paddingTop') {
            reservedStyle.paddingTop = style.rawPaddingTop;
            return;
        }
        if (key === 'paddingBottom') {
            reservedStyle.paddingBottom = style.rawPaddingBottom;
            return;
        }
        reservedStyle[key] = style[key];
    });

    // 处理缩放过的属性
    reservedStyle = processScaledStyle(reservedStyle);

    // 删除值为 null 或 undefined 的属性
    Object.keys(reservedStyle).forEach((key) => {
        if (expect(reservedStyle[key]).isNullOrUndefined()) {
            delete reservedStyle[key];
        }
    });
    if (Object.keys(reservedStyle).length) {
        return reservedStyle;
    }
    return null;
}

// 用于单元测试导出
export const testable = {
    getFixedInheritStyle,
    processNeedScaledStyle,
};
